import { supabase } from '../../../services/supabaseClient';
import { ETL_API } from '../../../config/etlApi';
import {
  emptyMqttConfig,
  getMqttConfigFromGateways,
} from './mqttDefaults';

export const MQTT_CONNECT_ERROR_MESSAGE =
  'Cannot connect to the MQTT server. Please try again or contact Chillverify support at support@chillverify.com';

const netlifyFunctionsBase = '/.netlify/functions';
const isDev = process.env.NODE_ENV === 'development';

/** Local uvicorn only — skip Cloud Run ETL probes in dev (returns 403 without IAM). */
const useLocalEtlBackend = () => isDev && !!process.env.REACT_APP_ETL_DEV_BACKEND;

export function formatMqttConnectError(raw) {
  if (!raw) return MQTT_CONNECT_ERROR_MESSAGE;

  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);

  if (
    text.includes('ETL backend unreachable')
    || text.includes('ECONNREFUSED')
    || text.includes('Failed to fetch')
    || text.includes('NetworkError')
    || text.includes('Unexpected token')
    || text.includes('Service temporarily unavailable')
  ) {
    return MQTT_CONNECT_ERROR_MESSAGE;
  }

  try {
    const parsed = JSON.parse(text);
    if (parsed.error && !parsed.error.includes('ETL backend')) {
      return parsed.error;
    }
    if (parsed.detail) {
      return MQTT_CONNECT_ERROR_MESSAGE;
    }
  } catch {
    // not JSON
  }

  return MQTT_CONNECT_ERROR_MESSAGE;
}

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

function mqttPayload(mqttConfig) {
  return {
    host: mqttConfig.host,
    port: mqttConfig.port,
    username: mqttConfig.username,
    password: mqttConfig.password,
    useTLS: mqttConfig.useTLS,
  };
}

/** Dev-only TCP check via CRA server (no Netlify functions on :8888 required). */
async function checkMqttBrokerHealthDev() {
  if (!isDev) return null;
  try {
    const response = await fetch('/dev/mqtt-broker-health');
    if (!response.ok) return null;
    const data = await response.json();
    return {
      brokerReachable: !!data.brokerReachable,
      online: false,
      connected: false,
      hasCredentials: false,
      host: data.host,
      port: data.port,
      source: 'dev-tcp',
    };
  } catch {
    return null;
  }
}

/** Load credentials from Supabase when Netlify functions are unavailable. */
async function fetchMqttBrokerConfigFromSupabase() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: gateways, error } = await supabase
    .from('gateways')
    .select('id, name, mqtt_broker_host, mqtt_broker_port, tbmq_username, tbmq_password')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const config = getMqttConfigFromGateways(gateways || []);
  const gateway = (gateways || []).find((g) => g.tbmq_username && g.tbmq_password);

  return {
    ...config,
    gateway_id: gateway?.id ?? null,
    gateway_name: gateway?.name ?? null,
  };
}

/** Cloud Run ETL backend session status (persistent MQTT for pipeline execution). */
export async function fetchMqttBrokerStatus() {
  if (isDev && !useLocalEtlBackend()) {
    return { connected: false, online: false };
  }

  try {
    const response = await fetch(`${ETL_API}/mqtt/status`);
    if (!response.ok) {
      return { connected: false, online: false };
    }
    const data = await response.json();
    return {
      connected: !!data.connected,
      online: !!data.connected,
      config: data.config || null,
    };
  } catch {
    return { connected: false, online: false };
  }
}

/** Ping broker — ETL session, Netlify health, or dev TCP fallback. */
export async function checkMqttBrokerHealth() {
  const etlStatus = await fetchMqttBrokerStatus();
  if (etlStatus.connected) {
    return {
      brokerReachable: true,
      online: true,
      connected: true,
      source: 'etl',
      host: etlStatus.config?.host,
      port: etlStatus.config?.port,
    };
  }

  try {
    const headers = await authHeaders();
    const response = await fetch(`${netlifyFunctionsBase}/pipeline-mqtt-health`, {
      method: 'GET',
      headers,
    });

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (response.ok && data?.success) {
        return {
          brokerReachable: !!data.brokerReachable,
          online: !!data.online,
          connected: false,
          hasCredentials: !!data.hasCredentials,
          host: data.host,
          port: data.port,
          gateway_name: data.gateway_name,
          source: 'netlify',
        };
      }
    }
  } catch {
    // fall through to dev TCP check
  }

  const devHealth = await checkMqttBrokerHealthDev();
  if (devHealth) {
    return devHealth;
  }

  return { brokerReachable: false, online: false, connected: false, hasCredentials: false };
}

/** Load broker + TBMQ credentials — Netlify function or Supabase fallback. */
export async function fetchMqttBrokerConfig() {
  try {
    const headers = await authHeaders();
    const response = await fetch(`${netlifyFunctionsBase}/pipeline-mqtt-config`, {
      method: 'GET',
      headers,
    });

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (response.ok && data?.success) {
        if (!data.config) {
          return {
            ...emptyMqttConfig(),
            gateway_id: null,
            gateway_name: null,
          };
        }

        const { host, port, username, password, useTLS } = data.config;
        return {
          host: host || '',
          port: port != null ? Number(port) : emptyMqttConfig().port,
          username: username || '',
          password: password || '',
          useTLS: !!useTLS,
          gateway_id: data.config.gateway_id,
          gateway_name: data.config.gateway_name,
        };
      }
    }
  } catch {
    // fall through to Supabase
  }

  return fetchMqttBrokerConfigFromSupabase();
}

async function connectViaNetlify(mqttConfig) {
  const headers = await authHeaders();
  const response = await fetch(`${netlifyFunctionsBase}/pipeline-mqtt-connect`, {
    method: 'POST',
    headers,
    body: JSON.stringify(mqttPayload(mqttConfig)),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text().catch(() => '');
    throw new Error(formatMqttConnectError(text));
  }

  const data = await response.json();
  if (!response.ok || !data?.success) {
    throw new Error(formatMqttConnectError(data?.error || data));
  }

  return { ...data, source: 'netlify' };
}

async function connectViaEtlBackend(mqttConfig) {
  const response = await fetch(`${ETL_API}/mqtt/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mqttPayload(mqttConfig)),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(formatMqttConnectError(text));
  }

  const data = await response.json();
  return { ...data, source: 'etl', success: true };
}

/** Connect — ETL when available locally or in prod; Netlify in local dev. */
export async function connectMqttBroker(mqttConfig) {
  if (useLocalEtlBackend() || !isDev) {
    try {
      return await connectViaEtlBackend(mqttConfig);
    } catch {
      return connectViaNetlify(mqttConfig);
    }
  }
  return connectViaNetlify(mqttConfig);
}
